/** Work Center (D-179) — ดู/สร้าง (Cost Center แยกไปหน้า CostCenterScreen); ไม่มีปุ่มลบ และแก้รหัส/โรงงานไม่ได้ (เป็นตัวตนของแถว) */

import { useState, type FormEvent } from 'react';

import { RecordPanel } from '../components/RecordPanel';
import { MachinePanel } from './MachinePanel';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import { patchRecord, postRecord, type RecordRow } from '../../lib/backend/records';
import { stockFailureText } from '../../lib/inventory/stock';

const MODULE = 'MASTER_DATA';

const STAGING_RULE_TEXT: Readonly<Record<string, string>> = {
  STAGING_LOCATION_NOT_FOUND: 'ไม่พบรหัสจุดพักวัตถุดิบนี้ในโรงงาน',
  STAGING_LOCATION_NOT_STAGING: 'ที่เก็บนี้ไม่ใช่ชนิด STAGING (จุดพักวัตถุดิบ)',
  STAGING_LOCATION_OTHER_PLANT: 'จุดพักวัตถุดิบนี้อยู่คนละโรงงานกับ Work Center',
  COST_CENTER_INACTIVE: 'Cost Center นี้ถูกปิดใช้งานแล้ว เลือก Cost Center อื่นหรือเปิดใช้งานก่อน',
};

function failureText(result: Parameters<typeof stockFailureText>[0]): string {
  if (result.status === 'rejected' && result.rule !== null && STAGING_RULE_TEXT[result.rule] !== undefined) {
    return STAGING_RULE_TEXT[result.rule] as string;
  }
  return stockFailureText(result);
}

export interface WorkCenterScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl?: string;
  readonly fetchImpl?: typeof fetch;
}

export function WorkCenterScreen({ permissions, apiBaseUrl, fetchImpl }: WorkCenterScreenProps) {
  const canCreate = can(permissions, MODULE, 'CREATE');
  const canEdit = can(permissions, MODULE, 'EDIT');
  const io = {
    ...(apiBaseUrl === undefined ? {} : { apiBaseUrl }),
    ...(fetchImpl === undefined ? {} : { fetchImpl }),
  };
  const [reload, setReload] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [wc, setWc] = useState({
    plant_code: '',
    work_center_code: '',
    name: '',
    cost_center_code: '',
    capacity_per_shift: '',
  });
  // D-190: hourly rates are optional (empty = not costed yet), so they stay out of the all-fields-required check
  const [rates, setRates] = useState({ labor_rate_per_hour: '', machine_rate_per_hour: '' });
  const [staging, setStaging] = useState('');
  const [editing, setEditing] = useState<{ id: string; code: string; value: string } | null>(null);
  const [machinesOf, setMachinesOf] = useState<{ plant: string; code: string } | null>(null);

  const create = async (event: FormEvent, path: string, body: Record<string, string>, reset: () => void) => {
    event.preventDefault();
    setError(null);
    setNotice(null);
    if (Object.values(body).some((v) => v.trim() === '')) {
      setError('กรอกให้ครบทุกช่อง');
      return;
    }
    const upper = (k: string, v: string) => (k.endsWith('_code') ? v.trim().toUpperCase() : v.trim());
    const optional = path === '/v1/work-centers' ? Object.entries(rates).filter(([, v]) => v.trim() !== '') : [];
    const stagingCode = path === '/v1/work-centers' ? staging.trim().toUpperCase() : '';
    const payload = Object.fromEntries([
      ...Object.entries(body).map(([k, v]) => [k, upper(k, v)]),
      ...optional.map(([k, v]) => [k, v.trim()]),
      ...(stagingCode === '' ? [] : [['staging_location_code', stagingCode]]),
    ]);
    const result = await postRecord(path, payload, {}, io);
    if (result.status !== 'ready') {
      setError(failureText(result));
      return;
    }
    setNotice('บันทึกแล้ว');
    reset();
    setReload((n) => n + 1);
  };

  const saveStaging = async (code: string | null) => {
    if (editing === null) {
      return;
    }
    setError(null);
    setNotice(null);
    const result = await patchRecord(
      `/v1/work-centers/${encodeURIComponent(editing.id)}`,
      { staging_location_code: code },
      {},
      io,
    );
    if (result.status !== 'ready') {
      setError(failureText(result));
      return;
    }
    setNotice(code === null ? 'ล้างจุดพักวัตถุดิบแล้ว' : 'บันทึกจุดพักวัตถุดิบแล้ว');
    setEditing(null);
    setReload((n) => n + 1);
  };

  const rowActions = (row: RecordRow) => (
    <>
      <button
        type="button"
        className="cap-button"
        onClick={() => {
          setMachinesOf({ plant: String(row.plant_code ?? ''), code: String(row.work_center_code ?? '') });
        }}
      >
        เครื่องจักร
      </button>
      {canEdit && (
        <button
          type="button"
          className="cap-button"
          onClick={() => {
            const current = row.staging_location_code;
            setEditing({
              id: String(row.work_center_id ?? ''),
              code: String(row.work_center_code ?? ''),
              value: typeof current === 'string' ? current : '',
            });
          }}
        >
          ตั้งจุดพัก
        </button>
      )}
    </>
  );

  const field = (label: string, value: string, onChange: (v: string) => void) => (
    <label className="cap-field" key={label}>
      <span className="cap-field__label">{label}</span>
      <input className="cap-input" aria-label={label} value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );

  return (
    <div className="cap-page" data-testid="work-centers">
      <div className="cap-page__head">
        <h1 className="cap-page__title">Work Center</h1>
      </div>
      {error !== null && (
        <p className="cap-notice__note" role="alert" data-testid="work-center-error">
          {error}
        </p>
      )}
      {notice !== null && (
        <p className="cap-notice__text" role="status" data-testid="work-center-notice">
          {notice}
        </p>
      )}

      <RecordPanel
        path="/v1/work-centers"
        columns={[
          { key: 'plant_code', label: 'โรงงาน' },
          { key: 'work_center_code', label: 'รหัส' },
          { key: 'name', label: 'ชื่อ' },
          { key: 'cost_center_code', label: 'Cost Center' },
          { key: 'capacity_per_shift', label: 'กำลังผลิต/กะ' },
          { key: 'labor_rate_per_hour', label: 'ค่าแรง บาท/ชม.' },
          { key: 'machine_rate_per_hour', label: 'ค่าเครื่อง+โสหุ้ย บาท/ชม.' },
          { key: 'staging_location_code', label: 'จุดพักวัตถุดิบ (Staging)' },
        ]}
        testId="work-center-table"
        reloadToken={reload}
        rowActions={rowActions}
        {...io}
      />
      {machinesOf !== null && (
        <MachinePanel
          plantCode={machinesOf.plant}
          workCenterCode={machinesOf.code}
          canCreate={canCreate}
          canEdit={canEdit}
          io={io}
          onClose={() => setMachinesOf(null)}
        />
      )}
      {canEdit && editing !== null && (
        <form
          className="cap-card cap-card--form cap-form"
          data-testid="work-center-staging-form"
          onSubmit={(e) => {
            e.preventDefault();
            const code = editing.value.trim().toUpperCase();
            if (code === '') {
              setError('กรอกรหัสจุดพักวัตถุดิบ หรือกด "ล้างจุดพัก"');
              return;
            }
            void saveStaging(code);
          }}
        >
          <div className="cap-form__grid">
            {field(`จุดพักวัตถุดิบของ ${editing.code}`, editing.value, (v) =>
              setEditing((c) => (c === null ? c : { ...c, value: v })),
            )}
          </div>
          <div className="cap-form__actions cap-form__actions--padded">
            <button type="submit" className="cap-button cap-button--primary">
              บันทึกจุดพัก
            </button>
            <button type="button" className="cap-button" onClick={() => void saveStaging(null)}>
              ล้างจุดพัก
            </button>
            <button type="button" className="cap-button" onClick={() => setEditing(null)}>
              ยกเลิก
            </button>
          </div>
        </form>
      )}
      {canCreate && (
        <form
          className="cap-card cap-card--form cap-form"
          data-testid="work-center-form"
          onSubmit={(e) =>
            void create(e, '/v1/work-centers', wc, () => {
              setWc({
                plant_code: '',
                work_center_code: '',
                name: '',
                cost_center_code: '',
                capacity_per_shift: '',
              });
              setRates({ labor_rate_per_hour: '', machine_rate_per_hour: '' });
              setStaging('');
            })
          }
        >
          <div className="cap-form__grid">
            {field('Work Center: โรงงาน', wc.plant_code, (v) => setWc((c) => ({ ...c, plant_code: v })))}
            {field('Work Center: รหัส', wc.work_center_code, (v) => setWc((c) => ({ ...c, work_center_code: v })))}
            {field('Work Center: ชื่อ', wc.name, (v) => setWc((c) => ({ ...c, name: v })))}
            {field('Work Center: Cost Center', wc.cost_center_code, (v) =>
              setWc((c) => ({ ...c, cost_center_code: v })),
            )}
            {field('Work Center: กำลังผลิตต่อกะ', wc.capacity_per_shift, (v) =>
              setWc((c) => ({ ...c, capacity_per_shift: v })),
            )}
            {field('Work Center: ค่าแรง บาท/ชม. (ไม่บังคับ)', rates.labor_rate_per_hour, (v) =>
              setRates((c) => ({ ...c, labor_rate_per_hour: v })),
            )}
            {field('Work Center: ค่าเครื่อง+โสหุ้ย บาท/ชม. (ไม่บังคับ)', rates.machine_rate_per_hour, (v) =>
              setRates((c) => ({ ...c, machine_rate_per_hour: v })),
            )}
            {field('จุดพักวัตถุดิบ (Staging) — ไม่บังคับ', staging, setStaging)}
          </div>
          <div className="cap-form__actions cap-form__actions--padded">
            <button type="submit" className="cap-button cap-button--primary">
              เพิ่ม Work Center
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
